import {BackendFetch} from "@hantera/portal-app"
import parseJson from "@/parse-json"
import {AppInfo, AppsApiError, AppSettingsValues} from "./apps.types"

function handleError(error: unknown): never {
  if (error instanceof AppsApiError) {
    throw error
  }

  if (error && typeof error === "object" && "response" in error) {
    const err = error as { response: { status: number; data?: { code?: string; message?: string } } }
    const status = err.response.status
    const data = err.response.data

    throw new AppsApiError(status, data?.code || "UNKNOWN_ERROR", data?.message || "Unknown error")
  }

  console.error(error)
  throw new AppsApiError(0, "NETWORK_ERROR", "Network error occurred")
}

export default function apps(fetch: BackendFetch) {
  return {
    async getApps(): Promise<AppInfo[]> {
      try {
        const response = await fetch("/core/resources/apps", {
          method: "GET",
        })

        if (!response.ok) {
          throw new AppsApiError(response.status, "FETCH_FAILED", "Failed to fetch apps")
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async getApp(id: string): Promise<AppInfo> {
      try {
        const response = await fetch(`/core/resources/apps/${encodeURIComponent(id)}`, {
          method: "GET",
        })

        if (!response.ok) {
          if (response.status === 404) {
            throw new AppsApiError(404, "NOT_FOUND", "App not found")
          }
          throw new AppsApiError(response.status, "FETCH_FAILED", "Failed to fetch app")
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async uploadPackage(file: File): Promise<AppInfo> {
      try {
        const formData = new FormData()
        formData.set("package", file)

        const response = await fetch("/core/resources/apps", {
          method: "PUT",
          body: formData,
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({})) as { code?: string; message?: string }
          throw new AppsApiError(
            response.status,
            errorData.code || "UPLOAD_FAILED",
            errorData.message || "Failed to upload app package"
          )
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async getSettings(appId: string): Promise<AppSettingsValues> {
      try {
        const response = await fetch(`/core/resources/apps/${encodeURIComponent(appId)}/settings`, {
          method: "GET",
        })

        if (!response.ok) {
          if (response.status === 404) {
            throw new AppsApiError(404, "NOT_FOUND", "App not found")
          }
          throw new AppsApiError(response.status, "FETCH_FAILED", "Failed to fetch app settings")
        }

        const data = parseJson(await response.text())
        return data
      } catch (error) {
        handleError(error)
      }
    },

    async patchSettings(appId: string, settings: AppSettingsValues): Promise<void> {
      try {
        const response = await fetch(`/core/resources/apps/${encodeURIComponent(appId)}/settings`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/merge-patch+json",
          },
          body: JSON.stringify(settings),
        })

        if (!response.ok) {
          if (response.status === 404) {
            throw new AppsApiError(404, "NOT_FOUND", "App not found")
          }
          const errorData = await response.json().catch(() => ({})) as { code?: string; message?: string }
          throw new AppsApiError(
            response.status,
            errorData.code || "PATCH_FAILED",
            errorData.message || "Failed to update app settings"
          )
        }
      } catch (error) {
        handleError(error)
      }
    },
  }
}
